/**
 * Rating Widget Komponente
 * Das Haupt-Custom-Element für das Bewertungssystem
 */
import { createStore } from '../utils/state.js'
import { createStaticStars, updateFilledStarsWidth } from './RatingStars.js'
import { RatingModal } from './RatingModal.js'
import { FeedbackModal } from './FeedbackModal.js'
import { initSecurity, canInteract, setDebugMode, startTracking } from '../utils/security.js'
import { submitRating, checkIfRated, submitFeedback } from '../utils/api.js'
import { saveRatingToStorage } from '../utils/storage.js'
import {
    MAX_STARS,
    FEEDBACK_DELAY_MS,
    STATUS_AUTO_HIDE_DELAY_MS,
    FEEDBACK_TEXT_ENDPOINT_TYPE,
    EMAIL_ENDPOINT_TYPE,
} from '../utils/constants.js'

export class RatingWidget extends HTMLElement {
    constructor() {
        super()
        // Store für State-Management erstellen
        this.store = createStore()

        // Komponenten-Variablen
        this.ratingModal = null
        this.feedbackModal = null
        this.unsubscribe = null // Für Store-Abonnement
    }

    /**
     * Wird aufgerufen, wenn das Element dem DOM hinzugefügt wird
     */
    connectedCallback() {
        // Attribute abrufen
        this.ratingPid = this.getAttribute('rating-pid')

        // Debug-Modus prüfen
        const debugMode = this.hasAttribute('debug')

        // Prüfen, ob es einen globalen Status für diese PID gibt
        let initialHasRated = false
        if (window.RatingGlobalState && window.RatingGlobalState.ratedPages) {
            initialHasRated = window.RatingGlobalState.ratedPages.get(this.ratingPid) || false
        }

        // Attribute im Store speichern
        this.store.setState({
            currentRating: this.parseRatingAttribute('current-rating'),
            isInteractive: this.hasAttribute('interactive'),
            debugMode: debugMode,
            showFeedbackModal: this.getAttribute('feedback-modal-show') === 'true',
            // feedbackModalLink removed
            // hasRated aus globalem State oder auf false setzen
            hasRated: initialHasRated,
        })

        // Übersetzungen laden
        this.loadTranslations()

        // Bot-Schutz initialisieren mit Debug-Modus
        initSecurity(debugMode)

        // Debug-Modus setzen
        if (debugMode) {
            setDebugMode(true)
            // Im Debug-Modus eine Basisinteraktion starten
            startTracking(true)
        }

        // Event-Listener für Rating-Synchronisation zwischen Widgets registrieren
        this.setupEventListeners()
        // Prüfen, ob Benutzer bereits bewertet hat
        this.checkAlreadyRated()

        // Komponente rendern und auf State-Änderungen reagieren
        this.unsubscribe = this.store.subscribe((state) => this.render(state))
    }

    /**
     * Wird aufgerufen, wenn das Element aus dem DOM entfernt wird
     */
    disconnectedCallback() {
        // Store-Abonnement beenden
        if (this.unsubscribe) {
            this.unsubscribe()
        }

        // Event-Listener entfernen
        document.removeEventListener('rating-submitted', this.handleRatingSubmittedEvent)
    }

    /**
     * Richtet die Event-Listener für die Widget-Synchronisation ein
     */
    setupEventListeners() {
        // Listener für Rating-Synchronisation zwischen Widgets
        this.handleRatingSubmittedEvent = (event) => {
            // Nur auf Events mit gleicher PID reagieren
            if (event.detail && event.detail.pid === this.ratingPid) {

                // Status aktualisieren ohne UI zu blockieren
                this.store.setState({
                    hasRated: true,
                    userRating: event.detail.rating,
                    currentRating: event.detail.averageRating || this.store.getState().currentRating,
                    // Im Debug-Modus interaktiv lassen, sonst deaktivieren
                    isInteractive: this.store.getState().debugMode,
                })

                // Interactive-Attribut entfernen, wenn nicht im Debug-Modus
                if (!this.store.getState().debugMode && this.hasAttribute('interactive')) {
                    this.removeAttribute('interactive')
                }

                // Widget neu rendern
                this.render(this.store.getState())
            }
        }

        // Event-Listener registrieren
        document.addEventListener('rating-submitted', this.handleRatingSubmittedEvent)
    }

    /**
     * Zu beobachtende Attribute
     */
    static get observedAttributes() {
        return [
            'rating-pid',
            'current-rating',
            'interactive',
            'debug',
            'feedback-modal-show',
            'feedback-modal-link',
            'text-already-rated',
            'text-submitting',
            'text-thank-you',
            'text-error',
            'text-tooltip',
            'text-bot-error',
            'modal-title',
            'modal-submit',
            'modal-cancel',
            'modal-header',
            'modal-description',
            'feedback-modal-title',
            'feedback-modal-header',
            'feedback-modal-description',
            'feedback-modal-input-label',
            'feedback-modal-submit',
            'feedback-modal-cancel',
            'feedback-modal-thank-you',
            'feedback-modal-high-title',
            'feedback-modal-high-description',
            'feedback-modal-low-title',
            'feedback-modal-low-description',
            'feedback-modal-email-label',
            'feedback-modal-privacy-text',
        ]
    }

    /**
     * Wird bei Attributänderungen aufgerufen
     */
    attributeChangedCallback(name, oldValue, newValue) {
        if (oldValue === newValue) return

        const state = this.store.getState()

        // State-Updates basierend auf Attribut
        switch (name) {
            case 'current-rating':
                this.store.setState({ currentRating: this.parseRatingAttribute(name) })
                break
            case 'rating-pid':
                this.ratingPid = newValue
                break
            case 'interactive':
                this.store.setState({ isInteractive: this.hasAttribute('interactive') })
                break
            case 'debug':
                const debugMode = this.hasAttribute('debug')
                this.store.setState({ debugMode: debugMode })
                // Debug-Modus für Security aktualisieren
                setDebugMode(debugMode)

                // Im Debug-Modus eine Basisinteraktion starten
                if (debugMode) {
                    startTracking(true)
                }
                break
            case 'feedback-modal-show':
                this.store.setState({ showFeedbackModal: this.getAttribute('feedback-modal-show') === 'true' })
                break

            default:
                // Text- und Modal-Attribute aktualisieren Übersetzungen
                if (name.startsWith('text-') || name.startsWith('modal-') || name.startsWith('feedback-')) {
                    this.loadTranslations()
                }
                break
        }
    }

    /**
     * Rating-Attribut parsen
     * @param {string} attrName - Der Name des Attributs
     * @returns {number} - Der geparste Wert oder 0
     */
    parseRatingAttribute(attrName) {
        const value = this.getAttribute(attrName)
        return value ? parseFloat(value) || 0 : 0
    }

    /**
     * Übersetzungen aus Attributen laden
     */
    loadTranslations() {
        // Wenn window.RatingWidgetTranslations vorhanden ist, verwenden wir diese
        // anstatt der Attribute
        if (window.RatingWidgetTranslations) {
            const translations = {
                alreadyRated: window.RatingWidgetTranslations.already_rated || '',
                submitting: window.RatingWidgetTranslations.submitting || '',
                thankYou: window.RatingWidgetTranslations.thank_you || '',
                error: window.RatingWidgetTranslations.error || '',
                tooltip: window.RatingWidgetTranslations.tooltip || '',
                botError: window.RatingWidgetTranslations.bot_error || '',
                modalTitle: window.RatingWidgetTranslations.modal_title || '',
                modalSubmit: window.RatingWidgetTranslations.modal_submit || '',
                modalCancel: window.RatingWidgetTranslations.modal_cancel || '',
                modalHeader: window.RatingWidgetTranslations.modal_header || '',
                modalDescription: window.RatingWidgetTranslations.modal_description || '',

                // Neue Feedback-Modal-Texte für die bedingte Darstellung
                feedbackModalSubmit: window.RatingWidgetTranslations.feedback_modal_submit || '',
                feedbackModalCancel: window.RatingWidgetTranslations.feedback_modal_cancel || '',
                feedbackModalThankYou: window.RatingWidgetTranslations.feedback_modal_thank_you || '',
                feedbackModalEmailLabel: window.RatingWidgetTranslations.feedback_modal_email_label || '',
                feedbackModalPrivacyText: window.RatingWidgetTranslations.feedback_modal_privacy_text || '',

                // Texte für hohe Bewertungen (4-5 Sterne)
                feedbackModalHighTitle: window.RatingWidgetTranslations.feedback_modal_high_title || '',
                feedbackModalHighDescription: window.RatingWidgetTranslations.feedback_modal_high_description || '',

                // Texte für niedrige Bewertungen (1-3 Sterne)
                feedbackModalLowTitle: window.RatingWidgetTranslations.feedback_modal_low_title || '',
                feedbackModalLowDescription: window.RatingWidgetTranslations.feedback_modal_low_description || '',
                feedbackModalInputLabel: window.RatingWidgetTranslations.feedback_modal_input_label || '',

                // Konfiguration
                feedbackModalHighRatingThreshold:
                    window.RatingWidgetTranslations.feedback_modal_high_rating_threshold || 4,
            }

            this.store.setState({ translations })
            return
        }

        // Fallback: Übersetzungen aus Attributen laden, falls window.RatingWidgetTranslations
        // nicht verfügbar ist (Abwärtskompatibilität)
        const translations = {
            alreadyRated: this.getAttribute('text-already-rated') || '',
            submitting: this.getAttribute('text-submitting') || '',
            thankYou: this.getAttribute('text-thank-you') || '',
            error: this.getAttribute('text-error') || '',
            tooltip: this.getAttribute('text-tooltip') || '',
            botError: this.getAttribute('text-bot-error') || '',
            modalTitle: this.getAttribute('modal-title') || '',
            modalSubmit: this.getAttribute('modal-submit') || '',
            modalCancel: this.getAttribute('modal-cancel') || '',
            modalHeader: this.getAttribute('modal-header') || '',
            modalDescription: this.getAttribute('modal-description') || '',
            feedbackModalTitle: this.getAttribute('feedback-modal-title') || '',
            feedbackModalHeader: this.getAttribute('feedback-modal-header') || '',
            feedbackModalDescription: this.getAttribute('feedback-modal-description') || '',
            feedbackModalInputLabel: this.getAttribute('feedback-modal-input-label') || '',
            feedbackModalSubmit: this.getAttribute('feedback-modal-submit') || '',
            feedbackModalCancel: this.getAttribute('feedback-modal-cancel') || '',
            feedbackModalThankYou: this.getAttribute('feedback-modal-thank-you') || '',

            // Neue Attribute für die bedingte Darstellung
            feedbackModalHighTitle: this.getAttribute('feedback-modal-high-title') || '',
            feedbackModalHighDescription: this.getAttribute('feedback-modal-high-description') || '',
            feedbackModalLowTitle: this.getAttribute('feedback-modal-low-title') || '',
            feedbackModalLowDescription: this.getAttribute('feedback-modal-low-description') || '',
            feedbackModalEmailLabel: this.getAttribute('feedback-modal-email-label') || '',
            feedbackModalPrivacyText: this.getAttribute('feedback-modal-privacy-text') || '',
        }

        this.store.setState({ translations })
    }

    /**
     * Debug-Informationen loggen wenn debugMode aktiv ist
     */
    logDebugInfo() {
        const state = this.store.getState()
        if (state.debugMode) {
        }
    }

    /**
     * Prüft, ob der Benutzer bereits bewertet hat
     */
    async checkAlreadyRated() {
        try {
            const state = this.store.getState()

            // Wenn keine PID vorhanden, abbrechen
            if (!this.ratingPid) {
                return
            }

            // Bewertungsstatus abrufen - auch im Debug-Modus prüfen
            const hasRated = await checkIfRated(this.ratingPid)

            // Wenn bereits bewertet, State aktualisieren
            if (hasRated) {

                // State aktualisieren
                // Im Debug-Modus zeigen wir den 'already-rated' Status an,
                // aber lassen den Button interaktiv für Testzwecke
                this.store.setState({
                    hasRated: true,
                    // Interactive nur deaktivieren wenn nicht im Debug-Modus
                    isInteractive: state.debugMode ? true : false,
                })

                // Globalen State aktualisieren und Event auslösen
                if (window.dispatchRatingStateEvent) {
                    window.dispatchRatingStateEvent(this.ratingPid, true)
                }


                // Interactive-Attribut nur entfernen, wenn nicht im Debug-Modus
                if (!state.debugMode && this.hasAttribute('interactive')) {
                    this.removeAttribute('interactive')
                }

                // Status sofort rendern um sicherzustellen, dass es angezeigt wird
                this.render(this.store.getState())
            } else {
            }
        } catch (error) {
        }
    }

    /**
     * Komponente basierend auf State rendern
     * @param {Object} state - Der aktuelle State
     */
    render(state) {
        // Inhalt leeren
        this.innerHTML = ''

        // Container erstellen
        const container = document.createElement('div')
        container.className = 'ratingwidget'

        // Prüfen, ob Rating-Anzeige sichtbar sein soll
        const showRatingDisplay = this.shouldShowRating()

        if (showRatingDisplay) {
            // Nicht-interaktive Sterne hinzufügen, die bei Klick das Modal öffnen
            const starsContainer = createStaticStars(state.currentRating, MAX_STARS, { showRatingText: true })

            // Breite der gefüllten Sterne korrekt setzen
            const filledStars = starsContainer.querySelector('.filled-stars')
            if (filledStars) {
                updateFilledStarsWidth(starsContainer, state.currentRating, MAX_STARS)
            }

            // Klick-Event zum Öffnen des Modals hinzufügen
            if (state.isInteractive && !state.hasRated) {
                starsContainer.addEventListener('click', () => {
                    // Nur fortfahren, wenn Interaktion erlaubt ist (nach Verzögerungsperiode)
                    if (canInteract()) {
                        this.openRatingModal()
                    }
                })
            }

            container.appendChild(starsContainer)
        } else {
            // Nur Nicht-interaktive Sterne hinzufügen (nur leere Sterne ohne gefüllte Sterne)
            const starsContainer = createStaticStars(0, MAX_STARS, { onlyEmptyStars: true })
            container.appendChild(starsContainer)
        }

        // Flex-Container für Status
        const infoContainer = document.createElement('div')
        infoContainer.className = 'info-container'

        // Status-Container für Feedback hinzufügen
        const statusContainer = document.createElement('div')
        statusContainer.className = 'status-container'

        // Wenn Benutzer bereits bewertet hat, Status direkt anzeigen, unabhängig von showRating
        if (state.hasRated) {
            setTimeout(() => {
                statusContainer.textContent = state.translations.alreadyRated
                statusContainer.className = 'status-container already-rated'
            }, 2000)
        } else {
            statusContainer.style.display = 'none'
        }

        infoContainer.appendChild(statusContainer)

        // Info-Container zum Haupt-Container hinzufügen
        container.appendChild(infoContainer)

        // Zum DOM hinzufügen
        this.appendChild(container)
    }

    /**
     * Öffnet das Rating-Modal für Benutzerinteraktion
     */
    openRatingModal() {
        const state = this.store.getState()

        // Wenn bereits bewertet und nicht im Debug-Modus, Modal nicht öffnen
        if (state.hasRated) {

            // Wenn nicht im Debug-Modus, Rating-Modal nicht öffnen
            if (!state.debugMode) {
                return
            }

            // Im Debug-Modus trotzdem öffnen aber Meldung ausgeben
        }

        // Modal nicht öffnen wenn nicht interaktiv
        if (!state.isInteractive) {
            return
        }

        // Prüfen, ob Interaktion nach Verzögerungsperiode erlaubt ist
        if (!canInteract()) {
            // Im Debug-Modus die Tracking-Daten erhöhen
            if (state.debugMode) {
                startTracking(true)
            }
            return
        }

        // Modal erstellen, wenn es nicht existiert
        if (!this.ratingModal) {
            this.ratingModal = new RatingModal({
                currentRating: state.currentRating,
                maxStars: MAX_STARS,
                ratingPid: this.ratingPid,
                onSubmit: (rating) => this.handleRatingSubmit(rating),
                translations: {
                    modalTitle: state.translations.modalTitle,
                    submitButton: state.translations.modalSubmit,
                    cancelButton: state.translations.modalCancel,
                    tooltip: state.translations.tooltip,
                    header: state.translations.modalHeader,
                    description: state.translations.modalDescription,
                    botError: state.translations.botError,
                },
            })
        }

        // Im Debug-Modus stellen wir sicher, dass die Tracking-Daten erhöht werden
        if (state.debugMode) {
            startTracking(true)
        }

        // Modal öffnen
        this.ratingModal.open()
    }

    /**
     * Prüft, ob die Rating-Anzeige sichtbar sein soll (basierend auf globaler Variable)
     * @returns {boolean} true, wenn die Rating-Anzeige sichtbar sein soll
     */
    shouldShowRating() {
        // Prüfen, ob die globale Variable showRating existiert und 0 ist
        return typeof window.showRating === 'undefined' || window.showRating !== 0
    }

    /**
     * Löst ein Event aus, um alle Widgets auf der Seite zu synchronisieren
     * @param {number} rating - Die abgegebene Bewertung
     * @param {number} averageRating - Die neue Durchschnittsbewertung
     */
    dispatchRatingEvent(rating, averageRating) {

        // Custom Event mit Rating-Daten erstellen
        const ratingEvent = new CustomEvent('rating-submitted', {
            bubbles: true,
            detail: {
                pid: this.ratingPid,
                rating: rating,
                averageRating: averageRating,
                timestamp: Date.now(),
            },
        })

        // Event auslösen
        document.dispatchEvent(ratingEvent)
    }

    /**
     * Behandelt die Bewertungsabgabe
     * @param {number} rating - Die abgegebene Bewertung
     */
    handleRatingSubmit(rating) {
        const state = this.store.getState()

        // Status-Container für Benutzerfeedback finden
        const statusContainer = this.querySelector('.status-container')

        // Status anzeigen - zuerst Ladezustand
        this.updateStatusContainer(statusContainer, state.translations.submitting, 'submitting')

        // Bewertung an den Server senden
        submitRating(this.ratingPid, rating)
            .then((response) => {
                // "Bereits bewertet" Fall behandeln
                if (response.alreadyRated) {
                    // Als bereits bewertet markieren
                    this.store.setState({ hasRated: true })

                    // Status-Container aktualisieren
                    this.updateStatusContainer(statusContainer, state.translations.alreadyRated, 'already-rated')

                    // Interactive-Attribut nur entfernen, wenn Debug-Modus nicht aktiviert ist
                    if (!state.debugMode) {
                        this.removeAttribute('interactive')
                        this.store.setState({ isInteractive: false })
                    }

                    return
                }

                // Erfolgsfall - UI mit Erfolgsmeldung aktualisieren
                if (!state.showFeedbackModal) {
                    this.updateStatusContainer(statusContainer, state.translations.thankYou, 'success')
                } else {
                    // Kurz Erfolg zeigen, bevor das Feedback-Modal geöffnet wird
                    this.updateStatusContainer(statusContainer, state.translations.thankYou, 'success')
                }

                // Aktuelle Bewertungsanzeige mit Durchschnittsbewertung vom Server aktualisieren
                if (response.averageRating) {
                    const newRating = response.averageRating

                    // State aktualisieren
                    this.store.setState({ currentRating: newRating })

                    // Rating-Textanzeige aktualisieren
                    const ratingText = this.querySelector('.rating-text')
                    if (ratingText) {
                        ratingText.textContent = newRating.toFixed(1)
                    }

                    // Gefüllte Sterne aktualisieren
                    const starsContainer = this.querySelector('.stars-container')
                    if (starsContainer) {
                        updateFilledStarsWidth(starsContainer, newRating, MAX_STARS)
                    }
                }

                // Als bewertet markieren und Bewertung im Store speichern
                this.store.setState({
                    hasRated: true,
                    userRating: rating,
                    isInteractive: state.debugMode ? true : false, // Im Debug-Modus interaktiv bleiben
                })

                // Globalen State aktualisieren und Event auslösen
                if (window.dispatchRatingStateEvent) {
                    window.dispatchRatingStateEvent(this.ratingPid, true)
                }

                // Event auslösen, um andere Widgets zu benachrichtigen
                this.dispatchRatingEvent(rating, response.averageRating)

                // Bewertung im localStorage speichern
                saveRatingToStorage(this.ratingPid, rating)

                // Interactive-Attribut nach erfolgreicher Übermittlung entfernen, nur wenn Debug-Modus nicht aktiviert ist
                if (!state.debugMode) {
                    this.removeAttribute('interactive')
                }

                // Wenn Feedback-Modal aktiviert ist, nach kurzer Verzögerung öffnen
                if (state.showFeedbackModal) {
                    setTimeout(() => {
                        this.openFeedbackModal()
                    }, FEEDBACK_DELAY_MS)
                } else {
                    // Erfolgsmeldung nach Verzögerung ausblenden, aber "bereits bewertet" Status für die Zukunft behalten
                    setTimeout(() => {
                        // Erfolgsmeldung durch "bereits bewertet" Pill ersetzen
                        this.updateStatusContainer(statusContainer, state.translations.alreadyRated, 'already-rated')
                    }, STATUS_AUTO_HIDE_DELAY_MS)
                }
            })
            .catch((error) => {
                this.updateStatusContainer(statusContainer, state.translations.error, 'error')
            })
    }

    /**
     * Status-Container aktualisieren
     * @param {HTMLElement} container - Der Status-Container
     * @param {string} text - Der anzuzeigende Text
     * @param {string} className - Die CSS-Klasse für den Container
     */
    updateStatusContainer(container, text, className) {
        if (!container) return

        container.textContent = text
        container.className = `status-container ${className}`
        container.style.display = 'inline-block'
    }

    /**
     * Öffnet das Feedback-Modal für zusätzliches Feedback
     */
    openFeedbackModal() {
        const state = this.store.getState()
        const userRating = state.userRating || 0
        const highRatingThreshold = state.translations.feedbackModalHighRatingThreshold || 4

        // Feedback-Modal erstellen, wenn es nicht existiert
        if (!this.feedbackModal) {
            this.feedbackModal = new FeedbackModal({
                ratingPid: this.ratingPid,
                ratingValue: userRating,
                linkUrl: '', // feedbackModalLink removed
                onSubmit: (data) => this.handleFeedbackSubmit(data),
                translations: {
                    // Allgemeine Texte
                    submitButton: state.translations.feedbackModalSubmit,
                    cancelButton: state.translations.feedbackModalCancel,
                    thankYou: state.translations.feedbackModalThankYou,
                    emailLabel: state.translations.feedbackModalEmailLabel,
                    privacyText: state.translations.feedbackModalPrivacyText,

                    // Texte für hohe Bewertungen (4-5 Sterne)
                    highTitle: state.translations.feedbackModalHighTitle,
                    highDescription: state.translations.feedbackModalHighDescription,

                    // Texte für niedrige Bewertungen (1-3 Sterne)
                    lowTitle: state.translations.feedbackModalLowTitle,
                    lowDescription: state.translations.feedbackModalLowDescription,
                    inputLabel: state.translations.feedbackModalInputLabel,

                    // Konfiguration
                    highRatingThreshold: highRatingThreshold,
                },
            })
        } else {
            // Aktualisiere die Bewertung
            this.feedbackModal.options.ratingValue = userRating
        }

        // Modal öffnen
        this.feedbackModal.open()
    }

    /**
     * Behandelt Feedback-Übermittlungen
     * @param {Object} data - Die Feedback-Daten
     */
    handleFeedbackSubmit(data) {
        const state = this.store.getState()
        const statusContainer = this.querySelector('.status-container')

        // Log im Debug-Modus

        // Lade-Status anzeigen
        if (statusContainer) {
            this.updateStatusContainer(statusContainer, state.translations.submitting, 'submitting')
        }

        // Feedback an den Server senden
        submitFeedback(data)
            .then((response) => {

                // Erfolgsstatus kurz anzeigen
                if (statusContainer) {
                    this.updateStatusContainer(
                        statusContainer,
                        response.message || state.translations.thankYou,
                        'success'
                    )
                }

                // Nach kurzer Verzögerung auf "bereits bewertet" zurücksetzen
                setTimeout(() => {
                    if (statusContainer) {
                        const currentState = this.store.getState()
                        this.updateStatusContainer(
                            statusContainer,
                            currentState.translations.alreadyRated,
                            'already-rated'
                        )
                    }
                }, STATUS_AUTO_HIDE_DELAY_MS)
            })
            .catch((error) => {

                // Fehlerstatus anzeigen
                if (statusContainer) {
                    this.updateStatusContainer(statusContainer, state.translations.error, 'error')
                }

                // Nach Verzögerung trotzdem auf "bereits bewertet" zurücksetzen
                setTimeout(() => {
                    if (statusContainer) {
                        const currentState = this.store.getState()
                        this.updateStatusContainer(
                            statusContainer,
                            currentState.translations.alreadyRated,
                            'already-rated'
                        )
                    }
                }, STATUS_AUTO_HIDE_DELAY_MS)
            })
    }
}

// Custom Element registrieren
customElements.define('rating-widget', RatingWidget)
